import { DateHelper } from "./lib/DateHelper"

export class OpeningHourHelper {
	static NEXT_DAY_LIMIT = 5

	/**
	 * 
	 * @param {Date} date 
	 * @param {[Rule]} ruleSet E.g.
	 * 
	 * 	[
	 * 		{ fromDay: 'mo', toDay: 'su', openingHour: '11:30', closingHour: '23:00' },
	 * 		{ fromDay: 'fr', toDay: 'sa', closingHour: '01:00' },
	 * 		{ fromDay: 'mo', toDay: 'mo', closed: true }
	 * 	]
	 * 
	 * @returns 
	 */
	static isOpen = (date, ruleSet) => {
		const timeline = OpeningHourHelper._getTimeline(date, null, ruleSet)
		return OpeningHourHelper._isOpen(date, timeline)
	}

	/**
	 * Return an array of alternating open/close Dates.  
	 * Example for closed starting with opening:
	 * 
	 * 	[
	 * 		{event: 'opening', date: new Date('2022-04-14T11:30:00')},
	 * 		{event: 'closing', date: new Date('2022-04-14T22:30:00')},
	 * 		{event: 'opening', date: new Date('2022-05-14T11:30:00')},
	 * 		{event: 'closing', date: new Date('2022-05-14T22:30:00')}
	 * 	]
	 * 
	 * Example for open starting with closing:
	 * 
	 * 	[
	 * 		{event: 'closing', date: new Date('2022-04-14T22:30:00')},
	 * 		{event: 'opening', date: new Date('2022-05-14T11:30:00')},
	 * 		{event: 'closing', date: new Date('2022-05-14T22:30:00')},
	 * 		{event: 'opening', date: new Date('2022-06-14T11:30:00')}
	 * 	]
	 * 
	 * @param {Date} fromDate 
	 * @param {Date} toDate If null only the next event is returned
	 * @param {[Rule]} ruleSet The given ruleSet e.G.
	 * 
	 * 	[
	 * 		{ fromDay: 'mo', toDay: 'su', openingHour: '11:30', closingHour: '23:00' },
	 * 		{ fromDay: 'mo', toDay: 'mo', closingHour: '23:30' },
	 * 		{ fromDay: 'tu', toDay: 'tu', closed: true }
	 * 	]
	 * @returns {[Event]}
	 */
	static _getTimeline = (fromDate, toDate, ruleSet) => {
		if (!ruleSet || ruleSet.length === 0) return []
		const events = []
		let priority = 1
		const reversedRules = ruleSet
			.concat([])
			.map(r => {
				r.priority = priority++
				return r
			})
			.reverse()
			.map(r => {
				if (r.closingHour) {
					let closingMinute = OpeningHourHelper._getMinuteOfDay(r.closingHour)
					if (closingMinute <= OpeningHourHelper.NEXT_DAY_LIMIT * 60) {
						const clone = JSON.parse(JSON.stringify(r))
						clone.closingHour = OpeningHourHelper._getHourMinute(closingMinute + DateHelper.MIN_OF_24_H)
						return clone
					}
					else {
						return r
					}
				}
				else {
					return r
				}
			})
		const fromTime = fromDate.getTime()
		const dayBefore = new Date(fromTime)
		dayBefore.setDate(dayBefore.getDate() - 1)
		const dateRunner = new Date(dayBefore)
		const toTime = toDate ? toDate.getTime() : null
		do {
			const dayOfWeekName = DateHelper.DAYS_OF_WEEK[dateRunner.getDay()]
			let openingTimestamp = null
			let closingTimestamp = null
			reversedRules
				.filter(r => OpeningHourHelper._isForDay(r, dayOfWeekName))
				.forEach(r => {
					if (!openingTimestamp && r.openingHour) {
						const openingDateStart = new Date(dateRunner.getFullYear(), dateRunner.getMonth(), dateRunner.getDate())
						openingDateStart.setMinutes(OpeningHourHelper._getMinuteOfDay(r.openingHour))
						openingTimestamp = openingDateStart
						events.push({ event: 'opening', timestamp: openingDateStart.getTime(), priority: r.priority })
					}
					if (!closingTimestamp && r.closingHour) {
						const closingDateStart = new Date(dateRunner.getFullYear(), dateRunner.getMonth(), dateRunner.getDate())
						closingDateStart.setMinutes(OpeningHourHelper._getMinuteOfDay(r.closingHour))
						closingTimestamp = closingDateStart
						events.push({ event: 'closing', timestamp: closingDateStart.getTime(), priority: r.priority })
					}
					if (!openingTimestamp && !closingTimestamp && r.closed) {
						const date = new Date(dateRunner.getFullYear(), dateRunner.getMonth(), dateRunner.getDate())
						openingTimestamp = date
						closingTimestamp = date
						events.push({ event: 'opening', timestamp: date.getTime(), priority: r.priority })
						events.push({ event: 'closing', timestamp: date.getTime(), priority: r.priority })
					}
				})
			dateRunner.setDate(dateRunner.getDate() + 1)
		} while ((!toDate && events.length < 6) || (toDate && dateRunner <= toDate))
		// Sort by timestamp asc, opening before closing
		const sortedEvents = events
			.filter(e => e.timestamp >= fromTime)
			.filter(e => !toTime || e.timestamp <= toTime)
			.sort((a, b) => a.event === 'opening' ? -1 : 1)
			.sort((a, b) => a.timestamp - b.timestamp)
		// Select event with highest priority from neighboring openings and closings
		let currentGroup = null
		const priorizedEvents = []
		sortedEvents.forEach(e => {
			if (currentGroup) {
				if (currentGroup.event !== e.event) {
					if (e.event === 'closing' && currentGroup.event === 'opening' && e.timestamp === currentGroup.timestamp) {
						// ignore current and previous element if 
						// 	the previous was an opening, 
						// 	the current is a closing 
						// 	and the timestamps are equal
						currentGroup = null
					}
					else {
						// flush group
						priorizedEvents.push(currentGroup)
						currentGroup = e
					}
				}
				else if (e.priority > currentGroup.priority) {
					// Update group
					currentGroup = e
				}
				// Else ignore event with same type and lower priority
			}
			else {
				// Start new group
				currentGroup = e
			}
		})
		if (currentGroup) {
			priorizedEvents.push(currentGroup)
		}
		return priorizedEvents;
	}

	static _isForDay = (rule, dayOfWeek) => {
		let dayOfWeekPos = DateHelper.DAY_INDICES[dayOfWeek]
		const ruleFromDayPos = DateHelper.DAY_INDICES[rule.fromDay]
		let ruleToDayPos = DateHelper.DAY_INDICES[rule.toDay]
		if (ruleToDayPos < ruleFromDayPos) {
			ruleToDayPos += 7
		}
		if (dayOfWeekPos < ruleFromDayPos) {
			dayOfWeekPos += 7
		}
		// TODO Consider hours after 0:00
		return dayOfWeekPos >= ruleFromDayPos && dayOfWeekPos <= ruleToDayPos
	}

	static _getMinuteOfDay = hourMinute => {
		let minuteOfDay = null
		if (hourMinute) {
			let matcher = hourMinute.match(/(\d+):(\d+)/)
			if (matcher.length === 3) {
				minuteOfDay = Number(matcher[1]) * 60 + Number(matcher[2])
			}
			else console.error('Invalid (h)h:mm format: ', hourMinute)
		}
		return minuteOfDay
	}

	static _getHourMinute = minuteOfDay => {
		let hourMinute = null
		if (minuteOfDay === 0 || Number(minuteOfDay) > 0) {
			const hour = Math.floor(minuteOfDay / 60)
			const minute = minuteOfDay - hour * 60
			hourMinute = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
		}
		return hourMinute
	}

	/**
	 * 
	 * @param {Date} date 
	 * @param {[Event]} timeline 
	 * @returns {boolean}
	 */
	static _isOpen = (date, timeline) => {
		const nextEvent = timeline.find(e => e.timestamp >= date.getTime())
		if (nextEvent) {
			if (nextEvent.event === 'closing') {
				return true
			}
			const time = date.getTime()
			if (nextEvent.event === 'opening' && nextEvent.timestamp === time) {
				return true
			}
		}
		return false
	}

	/**
	 * 
	 * @param {Date} date 
	 * @param {Date} now 
	 * @param {int} minutes 
	 * @returns 
	 */
	static isInFuture = (date, now, minutes) => date.getTime() > now.getTime() + minutes * 60 * 1000

	/**
	 * Consider preparation and delivery times
	 * 	
	 * @param {[Store]} stores 
	 * @param {boolean} isDelivery 
	 * @param {Date} date 
	 * @param {boolean} isScheduled 
	 * @returns {boolean} true if at least one store is open
	 */
	static areStoresOpen = (stores, isDelivery, date, isScheduled) => {
		const timestamp = date.getTime()
		let beginPreparationTime
		let endPreparationTime
		let beginDeliveryTime
		let endDeliveryTime
		if (isDelivery) {
			if (isScheduled) {
				beginPreparationTime = new Date(timestamp - 30 * 60 * 1000)
				endPreparationTime = new Date(timestamp - 15 * 60 * 1000)
				beginDeliveryTime = endPreparationTime
				endDeliveryTime = date
			}
			else {
				// ASAP
				beginPreparationTime = date
				endPreparationTime = new Date(timestamp + 15 * 60 * 1000)
				beginDeliveryTime = endPreparationTime
				endDeliveryTime = new Date(timestamp + 30 * 60 * 1000)
			}
		}
		else {
			// pickup
			if (isScheduled) {
				beginPreparationTime = new Date(timestamp - 15 * 60 * 1000)
				endPreparationTime = date
			}
			else {
				// ASAP
				beginPreparationTime = date
				endPreparationTime = new Date(timestamp + 15 * 60 * 1000)
			}
		}
		const now = new Date()
		if (
			!OpeningHourHelper.isInFuture(beginPreparationTime, now, -1)
			|| !OpeningHourHelper.isInFuture(endPreparationTime, now, -1)
			|| (beginDeliveryTime && !OpeningHourHelper.isInFuture(beginDeliveryTime, now, -1)) 
			|| (endDeliveryTime && !OpeningHourHelper.isInFuture(endDeliveryTime, now, -1))
		) {
			return false
		}
		return stores.some(s => {
			if (isDelivery) {
				return OpeningHourHelper.isOpen(beginPreparationTime, s.openingHours)
					&& OpeningHourHelper.isOpen(endPreparationTime, s.openingHours)
					&& OpeningHourHelper.isOpen(beginDeliveryTime, s.deliverer.openingHours)
					&& OpeningHourHelper.isOpen(endDeliveryTime, s.deliverer.openingHours)
			}
			else {
				// pickup
				return OpeningHourHelper.isOpen(beginPreparationTime, s.openingHours)
					&& OpeningHourHelper.isOpen(endPreparationTime, s.openingHours)
			}
		})
	}

	/**
	 * Snap to next interval
	 * @param {Date} date 
	 * @param {int} intervalMinutes 
	 * @returns {Date} A new date snapped to the next interval
	 */
	static snapNext = (date, intervalMinutes) => new Date(Math.ceil(date.getTime() / (1000 * 60 * intervalMinutes)) * intervalMinutes * 60 * 1000)

	/**
	 * Create schedules in an interval of interval and filter for open stores
	 * @param {Date} firstScheduleDate 
	 * @param {Date} latestScheduleDate 
	 * @param {int} interval Minutes
	 * @param {[Store]} stores 
	 * @param {boolean} isDelivery 
	 * @returns {[Date]} Schedules with open stores
	 */
	static getSchedules = (firstScheduleDate, latestScheduleDate, interval, stores, isDelivery) => {
		const latestScheduleTimestamp = latestScheduleDate.getTime()
		const intervalMs = interval * 60 * 1000
		const scheduleDates = []
		for (let scheduleTimestampRunner = firstScheduleDate.getTime(); scheduleTimestampRunner <= latestScheduleTimestamp; scheduleTimestampRunner += intervalMs) {
			const schedule = new Date(scheduleTimestampRunner)
			if (OpeningHourHelper.areStoresOpen(stores, isDelivery, schedule, true)) {
				scheduleDates.push(schedule)
			}
		}
		return scheduleDates
	}

}